import { DialogContentText, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { useSnackbar } from 'notistack';
import { useState, useMemo } from 'react';
import strings from '../../../localization';
import { useConnection } from '../../../utils/connection';
import { AIRDROP_BACKEND_URL } from '../../../utils/env-variables';
import { signAndSendTransaction, transferTokens } from '../../../utils/tokens';
import {
  TokenAccount,
  tokenAccountProgramId,
  UsdtScope,
  useUsdtAccounts,
} from '../../../utils/tokens/hooks';
import { estimateTransactionSize } from '../../../utils/transactions';
import { Wallet, useWallet } from '../../../utils/wallet';
import DialogForm from '../../DialogForm';

const MINIMUM_USDT_EXCHANGE_AMOUNT = 1.2;

const EXCHANGE_API_BASE = 'https://airdrop2.domichain.io/v1/exchange';

// The exchange flow is fire-and-forget on-chain: tokens leave the wallet
// before the backend acts. Sending is therefore gated on a live health
// probe so funds cannot be sent while the service is down or unfunded.
const HEALTH_PROBE_TIMEOUT_MS = 7000;
const HEALTH_REFETCH_INTERVAL_MS = 30 * 1000;

// Display symbol for the item being exchanged; legacy USDT and wUSDT are
// separate wallet items backed by different token programs.
function usdtScopeSymbol(scope: UsdtScope) {
  return scope === 'wusdt' ? 'wUSDT' : 'USDT';
}

// True only when the service confirms it can verify and deliver (database,
// RPC and funded sender). Any transport or parse failure counts as down.
async function fetchExchangeServiceHealthy(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(`${EXCHANGE_API_BASE}/health`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      return false;
    }
    const json = await response.json();
    return json?.status === 'success' && json?.data?.healthy === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export default function UsdtExchangeDialog({
  open,
  onClose,
  scope = 'legacy',
}: {
  open: boolean;
  onClose: () => void;
  scope?: UsdtScope;
}) {
  const wallet = useWallet();
  const connection = useConnection();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const tokenSymbol = usdtScopeSymbol(scope);
  const { data: tokenAccounts } = useUsdtAccounts(scope);

  const availableAmount = (tokenAccounts ?? []).reduce((acc, curr) => {
    const tokenAmount = curr.account.data.parsed.info.tokenAmount;
    return acc + tokenAmount.uiAmount;
  }, 0);

  const [inputAmount, setInputAmount] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const parsedInputAmount = /^\d+\.?\d*$/.test(inputAmount)
    ? new Decimal(inputAmount)
    : new Decimal(0);

  // Passive gate: keeps the Buy button disabled while the service is down.
  // The authoritative check runs again immediately before tokens are sent.
  const { data: serviceHealthy } = useQuery({
    queryKey: ['exchangeServiceHealth'],
    queryFn: fetchExchangeServiceHealthy,
    enabled: open,
    refetchInterval: HEALTH_REFETCH_INTERVAL_MS,
    retry: false,
  });

  const { mutate, isLoading } = useMutation({
    // Inputs are passed explicitly at confirm time. Reading dialog state
    // from the closure raced the post-confirm input reset and could submit
    // a zero-amount exchange (an instruction-less on-chain transaction).
    mutationFn: async ({
      amount,
      exchangeRate,
      expectedDomi,
    }: {
      amount: Decimal;
      exchangeRate: number;
      expectedDomi: string;
    }) => {
      if (!tokenAccounts) {
        return;
      }
      if (amount.lessThanOrEqualTo(0)) {
        throw new Error('Enter an amount above zero.');
      }
      if (!exchangeRate || exchangeRate <= 0) {
        throw new Error('Exchange rate unavailable. Please try again.');
      }

      const id = enqueueSnackbar('Sending transactions...', {
        variant: 'info',
        persist: true,
      });

      try {
        // Hard gate: nothing leaves the wallet unless the service that
        // delivers DOMI is reachable and able to pay out right now.
        const healthyNow = await fetchExchangeServiceHealthy();
        if (!healthyNow) {
          throw new Error(
            'Exchange service is unavailable right now. Nothing was sent — please try again later.',
          );
        }

        const signatures = await sendTransferTransactions(
          wallet,
          connection,
          tokenAccounts,
          amount,
        );

        // Send both signatures and exchange rate info to backend
        const response = await fetch(`${EXCHANGE_API_BASE}/order`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userWalletAddress: wallet.publicKey.toBase58(),
            signatures,
            exchangeData: {
              usdtAmount: amount.toString(),
              exchangeRate,
              expectedDomi,
              timestamp: Date.now()
            }
          }),
        });

        // The endpoint answers HTTP 200 even on failure; the real outcome
        // is inside the response envelope.
        let json: any = null;
        try {
          json = await response.json();
        } catch {
          // Non-JSON body; rejected below.
        }

        if (
          !response.ok ||
          json?.status !== 'success' ||
          json?.data?.success !== true
        ) {
          throw new Error(json?.data?.error ?? 'Exchange failed');
        }

        closeSnackbar(id);
        enqueueSnackbar(`${tokenSymbol} sent successfully. Your DOMI will arrive shortly.`, {
          variant: 'success',
          autoHideDuration: 5000,
        });

      } catch (err) {
        if (err instanceof Error) {
          closeSnackbar(id);
          enqueueSnackbar(err.message, { variant: 'error' });
          console.error(err);
        }
      }
    },
  });

  // Price fetching
  const { data: priceData, isLoading: isPriceLoading } = useQuery({
    queryKey: ['mexc-price'],
    queryFn: async () => {
      const response = await fetch(`${EXCHANGE_API_BASE}/price`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      // console.log('Frontend: Response status:', response.status);

      if (!response.ok) {
        console.error('Frontend: Failed to fetch price, status:', response.status);
        throw new Error('Failed to fetch DOMI price');
      }

      const data = await response.json();
      // console.log('Frontend: Received price data:', data);

      // The API returns { status: 'success', data: { success: true, price: number } }
      if (data.status !== 'success' || !data.data.success) {
        console.error('Frontend: Price fetch unsuccessful:', data.error);
        throw new Error(data.error || 'Failed to fetch price');
      }

      return data.data.price;
    },
    refetchInterval: 10000,
    onError: (error) => {
      console.error('Frontend: Query error:', error);
    }
  });

  const estimatedDomi = useMemo(() => {
    if (!priceData || !parsedInputAmount) return null;
    return parsedInputAmount.dividedBy(priceData).toFixed(4);
  }, [priceData, parsedInputAmount]);

  const validAmount = useMemo(() => {
    if (!parsedInputAmount || !priceData) return false;

    return parsedInputAmount.greaterThanOrEqualTo(MINIMUM_USDT_EXCHANGE_AMOUNT) &&
           parsedInputAmount.lessThanOrEqualTo(availableAmount);
  }, [parsedInputAmount, availableAmount, priceData]);

  const isDisabled = !validAmount || isLoading || serviceHealthy === false;

  const amountError = useMemo(() => {
    if (!parsedInputAmount) return '';
    if (parsedInputAmount.lessThan(MINIMUM_USDT_EXCHANGE_AMOUNT)) {
      return `Minimum amount is ${MINIMUM_USDT_EXCHANGE_AMOUNT} ${tokenSymbol}`;
    }
    if (parsedInputAmount.greaterThan(availableAmount)) {
      return 'Insufficient balance';
    }
    return '';
  }, [parsedInputAmount, availableAmount, tokenSymbol]);

  return (
    <>
      <DialogForm
        open={open}
        onClose={onClose}
        fullWidth
      >
        <DialogTitle>{strings.buyDomiDialogTitle}</DialogTitle>
        <DialogContent>
          {isPriceLoading ? (
            <DialogContentText>
              Loading current exchange rate...
            </DialogContentText>
          ) : (
            <DialogContentText>
              Current Rate: 1 DOMI = {priceData?.toFixed(4)} USDT
            </DialogContentText>
          )}

          {serviceHealthy === false && (
            <DialogContentText color="error">
              The exchange service is currently unavailable. Buying is
              disabled so your {tokenSymbol} cannot be sent while DOMI
              delivery is not guaranteed.
            </DialogContentText>
          )}

          <TextField
            label={strings.amount}
            fullWidth
            variant="outlined"
            margin="normal"
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setInputAmount(availableAmount.toFixed(9))}
                  >
                    {strings.max}
                  </Button>
                  <Typography color="primary">{tokenSymbol}</Typography>
                </InputAdornment>
              ),
              inputProps: {
                step: 10 ** -9,
              },
            }}
            value={inputAmount}
            onChange={(e) => setInputAmount(e.target.value.trim())}
            error={!!amountError}
            helperText={
              amountError || (
                <span onClick={() => setInputAmount(availableAmount.toFixed(9))}>
                  {strings.max}: {availableAmount.toFixed(9)}
                </span>
              )
            }
          />

          {estimatedDomi && (
            <DialogContentText>
              You will receive approximately {estimatedDomi} DOMI
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div>
            <Button onClick={onClose}>{strings.cancel}</Button>
            <Button
              color="primary"
              disabled={isDisabled || isPriceLoading || !estimatedDomi || !validAmount}
              onClick={() => setShowConfirm(true)}
            >
              {strings.buyButton}
            </Button>
          </div>
        </DialogActions>
      </DialogForm>

      <ConfirmExchangeDialog
              open={showConfirm}
              onClose={() => setShowConfirm(false)}
              usdtAmount={inputAmount}
              tokenSymbol={tokenSymbol}
              estimatedDomi={estimatedDomi || '0'}
              exchangeRate={priceData || 0}
              onConfirm={() => {
                setShowConfirm(false);
                // Snapshot the inputs into the mutation BEFORE the field
                // reset below; the mutation must never depend on dialog
                // state that is about to clear. The user returns to the
                // balances list; progress is reported via snackbars.
                mutate({
                  amount: parsedInputAmount,
                  exchangeRate: priceData || 0,
                  expectedDomi: estimatedDomi || '0',
                });
                setInputAmount('');
                onClose();
              }}
            />
    </>
  );
}

async function getTransferDestination() {
  const response = await fetch(
    `${AIRDROP_BACKEND_URL}/v1/get-exchange-address`,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch exchange destination address');
  }

  const responseJson = await response.json();
  const destinationPublicKey = responseJson.data?.publicKey;

  if (!destinationPublicKey) {
    throw new Error('Failed to get exchange destination address');
  }
  return new PublicKey(destinationPublicKey);
}

async function sendTransferTransactions(
  wallet: Wallet,
  connection: Connection,
  tokenAccounts: TokenAccount[],
  amount: Decimal,
) {
  const signatures: Array<string> = [];
  const destinationPublicKey = new PublicKey('DWXcudFyubtXZboMtiz58hDCRSwAvhi4MTpUe1svxmR8');

  const accountsForTransfer = findSuitableAccountsForTransfer(
    tokenAccounts,
    amount,
  );

  // Never sign an empty transaction: with no selected accounts the final
  // send below would broadcast an instruction-less transaction on-chain.
  if (accountsForTransfer.length === 0) {
    throw new Error('No token accounts selected for transfer');
  }

  let transaction = new Transaction();

  for (const { account, transferAmount } of accountsForTransfer) {
    const transferTransaction = await transferTokens({
      connection,
      owner: wallet,
      sourcePublicKey: account.pubkey,
      destinationPublicKey,
      amount: BigInt(
        transferAmount
          .mul(10 ** account.account.data.parsed.info.tokenAmount.decimals)
          .toString(),
      ),
      mint: new PublicKey(account.account.data.parsed.info.mint),
      decimals: account.account.data.parsed.info.tokenAmount.decimals,
      overrideDestinationCheck: true,
      // Each account signs under its owning program: legacy mints under
      // the USDT program, wUSDT under the main token program.
      programId: tokenAccountProgramId(account),
      memo: null,
    });

    const estimatedTransactionSize = estimateTransactionSize(
      new Transaction().add(transaction).add(transferTransaction),
      wallet.publicKey,
    );

    if (estimatedTransactionSize < 1232) {
      transaction.add(transferTransaction);
      continue;
    }

    // Send transaction and get signature
    const signature = await signAndSendTransaction(connection, transaction, wallet, []);

    // Wait for confirmation before proceeding
    console.log('Waiting for transaction confirmation:', signature);
    await connection.confirmTransaction(signature, 'confirmed');
    console.log('Transaction confirmed:', signature);

    signatures.push(signature);
    transaction = transferTransaction;
  }

  // Process final transaction
  const finalSignature = await signAndSendTransaction(connection, transaction, wallet, []);

  // Wait for final transaction confirmation
  console.log('Waiting for final transaction confirmation:', finalSignature);
  await connection.confirmTransaction(finalSignature, 'confirmed');
  console.log('Final transaction confirmed:', finalSignature);

  signatures.push(finalSignature);

  return signatures;
}

function findSuitableAccountsForTransfer(
  accounts: TokenAccount[],
  transferAmount: Decimal,
) {
  const result: Array<{ account: TokenAccount; transferAmount: Decimal }> = [];
  const sortedAccounts = accounts
    .slice()
    .sort((a, b) =>
      Number(
        b.account.data.parsed.info.tokenAmount.uiAmount -
          a.account.data.parsed.info.tokenAmount.uiAmount,
      ),
    );

  let remainingTransferAmount = transferAmount;

  for (const account of sortedAccounts) {
    if (remainingTransferAmount.lessThanOrEqualTo(0)) {
      break;
    }
    const maxTransfer = Decimal.min(
      account.account.data.parsed.info.tokenAmount.uiAmountString,
      remainingTransferAmount,
    );
    result.push({ account, transferAmount: maxTransfer });
    remainingTransferAmount = remainingTransferAmount.sub(maxTransfer);
  }

  if (remainingTransferAmount.greaterThan(0.1)) {
    throw new Error('Insufficient funds');
  }

  return result;
}

function ConfirmExchangeDialog({
  open,
  onClose,
  usdtAmount,
  tokenSymbol,
  estimatedDomi,
  exchangeRate,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  usdtAmount: string;
  tokenSymbol: string;
  estimatedDomi: string;
  exchangeRate: number;
  onConfirm: () => void;
}) {
  return (
    <DialogForm open={open} onClose={onClose} fullWidth>
      <DialogTitle>Confirm Exchange</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You are about to exchange:
        </DialogContentText>
        <Typography variant="h6" gutterBottom>
          {usdtAmount} {tokenSymbol} for approximately {estimatedDomi} DOMI
        </Typography>
        <DialogContentText>
          Exchange Rate: 1 DOMI = {exchangeRate} USDT
        </DialogContentText>
        <DialogContentText color="textSecondary">
          The exact amount of DOMI may vary slightly based on market conditions when the exchange is executed.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="primary">
          Confirm Exchange
        </Button>
      </DialogActions>
    </DialogForm>
  );
}
